import { Plus, Trash2, Sparkles, Loader2, AlertTriangle, Pencil } from "lucide-react";
import { useTranslations } from "use-intl";
import type { EnglishMessages } from "@financialharmony/i18n/messageTypes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DOMESTIC_VAT_RATES } from "@financialharmony/tax-se";
// The column list both apps' line-items tables must agree on. Only the labels are shared —
// each app keeps its own markup, since the grid widths and UI kit legitimately differ.
import { LINE_ITEM_COLUMN_LABELS } from "@financialharmony/invoicing";
import type { Article, LineItem } from "@/services/invoiceGeneration";
import { num } from "@/services/invoiceGeneration";

interface Props {
  lines: LineItem[];
  articles: Article[];
  onChange: (id: string, patch: Partial<LineItem>) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onPickArticle: (id: string, article: Article) => void;
  /** Line ids currently awaiting the real LLM VAT classification (see classifyLineVat). */
  classifyingLineIds?: Set<string>;
  /**
   * FIN-280 AC2 — display-only: line ids whose description has changed since it was last
   * classified, but whose classify debounce hasn't fired yet. Rendered identically to
   * classifyingLineIds (same spinner/hint) so no line ever shows a confident-looking rate
   * in that window — never affects classifyingLineIds itself or Save/Issue gating.
   */
  pendingLineIds?: Set<string>;
  /** Line ids whose classification failed — VAT is unknown, issuing is blocked until retried. */
  classifyErrorLineIds?: Set<string>;
  onRetryClassify?: (lineId: string) => void;
  /**
   * False for a cross-border invoice, where 0% VAT plus reverse-charge/export wording is
   * legally required — the picker is hidden rather than offering a choice the backend
   * would refuse anyway (FIN-603 EC13). Defaults to true so an unaware caller gets the
   * domestic behaviour, which is what every existing caller means.
   */
  canOverrideVat?: boolean;
}

type Translator = ReturnType<typeof useTranslations>;

// The catalog key for each column the package lists; a new package column fails to compile until it has one.
const COLUMN_KEYS: Record<(typeof LINE_ITEM_COLUMN_LABELS)[number], keyof EnglishMessages["consultantReceivables"]["lineItems"]["columns"]> = {
  Description: "description",
  Qty: "quantity",
  Unit: "unit",
  "Unit price": "unitPrice",
  VAT: "vat",
};

/** The VAT cell's accessible name when it is a read-only display rather than a picker. */
function vatCellLabel(t: Translator, classifying: boolean, classifyError: boolean): string {
  if (classifying) return t("vatCell.classifying");
  if (classifyError) return t("vatCell.failed");
  return t("vatCell.fixedZero");
}

function vatCellContent(classifying: boolean, classifyError: boolean, vatRate: number) {
  if (classifying) return <Loader2 size={14} className="animate-spin" />;
  if (classifyError) return <AlertTriangle size={14} />;
  return `${vatRate}%`;
}

/**
 * Handing a line back to the engine. The rate is only reset when the engine's own answer
 * was actually recorded — a line saved before FIN-603 has none, and resetting it to 0 (a
 * real VAT rate here, momsfri) would be worse than leaving the current one in place.
 */
function clearOverridePatch(line: LineItem): Partial<LineItem> {
  if (typeof line.vatRateAutomatic !== "number") return { vatManual: false };
  return { vatManual: false, vatRate: line.vatRateAutomatic };
}

/**
 * The hint under a line: what the engine is doing, what went wrong, or that a human
 * overrode the rate. Extracted so the row itself stays readable — this is the one part
 * with four mutually exclusive states.
 */
function LineVatHint({ line, classifying, classifyError, matchesArticle, onChange, onRetryClassify }: {
  line: LineItem;
  classifying: boolean;
  classifyError: boolean;
  matchesArticle: boolean;
  onChange: (id: string, patch: Partial<LineItem>) => void;
  onRetryClassify?: (lineId: string) => void;
}) {
  const t = useTranslations("consultantReceivables.lineItems.hint");
  if (classifying) {
    return (
      <p className="flex items-center gap-1 pl-1 text-xs text-muted-foreground">
        <Loader2 size={12} className="animate-spin text-primary" /> {t("classifying")}
      </p>
    );
  }
  if (classifyError) {
    return (
      <p className="flex items-center gap-2 pl-1 text-xs text-destructive">
        <AlertTriangle size={12} />
        {t("failed")}
        <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs text-destructive underline" onClick={() => onRetryClassify?.(line.id)}>
          {t("retry")}
        </Button>
      </p>
    );
  }
  if (line.vatManual) {
    return <ManualVatHint line={line} onUseAutomatic={() => onChange(line.id, clearOverridePatch(line))} />;
  }
  if (!line.vatReason && !matchesArticle) return null;
  return (
    <p className="flex items-center gap-1 pl-1 text-xs text-muted-foreground">
      <Sparkles size={12} className="text-primary" />
      {line.vatReason
        ? t("automaticWithReason", { rate: String(line.vatRate), reason: line.vatReason })
        : t("automatic", { rate: String(line.vatRate) })}
    </p>
  );
}

/**
 * The marker on a line whose rate a human chose. The engine's own answer stays visible
 * next to it — that contrast IS the signal (F7); showing only the chosen rate would make
 * an override look like the system's own decision.
 */
function ManualVatHint({ line, onUseAutomatic }: { line: LineItem; onUseAutomatic: () => void }) {
  const t = useTranslations("consultantReceivables.lineItems.hint");
  const automaticRate = line.vatRateAutomatic;
  const automaticKey = line.vatReason ? "manualAutomaticWithReason" : "manualAutomatic";
  const automatic = typeof automaticRate === "number"
    ? t(automaticKey, { rate: String(automaticRate), reason: line.vatReason ?? "" })
    : "";
  return (
    <p className="flex items-center gap-1 pl-1 text-xs text-muted-foreground">
      <Pencil size={12} className="text-primary" />
      <span className="font-medium text-foreground">{t("manual")}</span>
      {automatic}
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs underline"
        onClick={onUseAutomatic}
      >
        {t("useAutomatic")}
      </Button>
    </p>
  );
}

export function LineItemsTable({ lines, articles, onChange, onAdd, onRemove, onPickArticle, classifyingLineIds, pendingLineIds, classifyErrorLineIds, onRetryClassify, canOverrideVat = true }: Props) {
  const t = useTranslations("consultantReceivables.lineItems");
  return (
    <div className="space-y-2">
      <div className="hidden md:grid grid-cols-[1fr_72px_72px_96px_112px_36px] gap-2 px-1 text-xs font-medium uppercase text-muted-foreground">
        {LINE_ITEM_COLUMN_LABELS.map((label) => <span key={label}>{t(`columns.${COLUMN_KEYS[label]}`)}</span>)}
        <span />
      </div>

      {lines.map((line) => {
        const match = articles.find(
          (a) => a.name.toLowerCase() === line.description.trim().toLowerCase(),
        );
        // FIN-280 AC2: pending is rendered identically to classifying (same spinner/hint) —
        // purely a display concern, never a separate boolean threaded through below.
        const classifying = (classifyingLineIds?.has(line.id) ?? false) || (pendingLineIds?.has(line.id) ?? false);
        const classifyError = classifyErrorLineIds?.has(line.id) ?? false;
        return (
          <div key={line.id} className="space-y-1">
            <div className="grid grid-cols-2 md:grid-cols-[1fr_72px_72px_96px_112px_36px] gap-2 items-center">
              <div>
                <Input
                  list={`articles-${line.id}`}
                  placeholder={t("columns.description")}
                  value={line.description}
                  onChange={(e) => {
                    const description = e.target.value;
                    const picked = articles.find(
                      (a) => a.name.toLowerCase() === description.trim().toLowerCase(),
                    );
                    if (picked) onPickArticle(line.id, picked);
                    else onChange(line.id, { description });
                  }}
                />
                <datalist id={`articles-${line.id}`}>
                  {articles.map((a) => (
                    <option key={a.id} value={a.name} />
                  ))}
                </datalist>
              </div>
              <Input
                type="number"
                inputMode="decimal"
                placeholder={t("columns.quantity")}
                aria-label={t("quantityLabel")}
                value={line.quantity}
                onChange={(e) => onChange(line.id, { quantity: Number(e.target.value) })}
              />
              <Input
                placeholder={t("columns.unit")}
                aria-label={t("columns.unit")}
                value={line.unit}
                onChange={(e) => onChange(line.id, { unit: e.target.value })}
              />
              <Input
                type="number"
                inputMode="decimal"
                placeholder={t("columns.unitPrice")}
                aria-label={t("columns.unitPrice")}
                value={line.unitPrice}
                onChange={(e) => onChange(line.id, { unitPrice: Number(e.target.value) })}
              />
              {classifying || classifyError || !canOverrideVat ? (
                <div
                  className={`flex h-9 items-center justify-center rounded-md border text-sm ${
                    classifyError ? "border-destructive bg-destructive/10 text-destructive" : "border-input bg-muted/40 text-muted-foreground"
                  }`}
                  aria-label={vatCellLabel(t, classifying, classifyError)}
                  // A cross-border invoice legally must carry 0% VAT and reverse-charge
                  // wording, so the picker is hidden. The backend refuses the override
                  // regardless (FIN-603 EC13) — this only avoids offering it.
                  title={canOverrideVat ? undefined : t("vatCell.fixedZeroTitle")}
                >
                  {vatCellContent(classifying, classifyError, line.vatRate)}
                </div>
              ) : (
                <Select
                  value={String(line.vatRate)}
                  onValueChange={(v) => onChange(line.id, { vatRate: Number(v), vatManual: true })}
                >
                  <SelectTrigger className="h-9" aria-label={t("vatRateLabel")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Sourced from packages/tax-se's rate->account maps, never a literal
                        list: salesEntryBuilder hard-throws for any rate with no confirmed
                        BAS account, so the options offered and the bookable set must not
                        be able to drift apart. */}
                    {DOMESTIC_VAT_RATES.map((rate) => (
                      <SelectItem key={rate} value={String(rate)}>{rate}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => onRemove(line.id)}
                aria-label={t("removeLine")}
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <LineVatHint
              line={line}
              classifying={classifying}
              classifyError={classifyError}
              matchesArticle={Boolean(match)}
              onChange={onChange}
              onRetryClassify={onRetryClassify}
            />
          </div>
        );
      })}

      <Button type="button" variant="ghost" size="sm" onClick={onAdd} className="text-primary">
        <Plus size={16} className="mr-1" /> {t("addLine")}
      </Button>

      {lines.some((l) => l.quantity * l.unitPrice > 0) && (
        <p className="pl-1 text-xs text-muted-foreground">
          {t("subtotal", { amount: num(lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0)) })}
        </p>
      )}
    </div>
  );
}
